import React, { useState, useRef, useEffect, useCallback } from 'react';
import usePortalUser from '../../auth/usePortalUser';
import { getCurrentSession } from '../../auth/cognito';
import ChangePasswordForm from '../../components/ChangePasswordForm';
import EmailClient from '../../components/EmailClient';
import './Portal.css';
import './ConsultantPortal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

/* ─── Calendar grid constants ────────────────────────────────────────────────
   Defined first so the mock-data IIFE below can call the helper functions.
   ─────────────────────────────────────────────────────────────────────────── */
const CAL_START_HOUR = 6;
const CAL_END_HOUR   = 21;
const SLOT_MINS      = 30;
const SLOT_H         = 24;   // px per 30-min slot
const TOTAL_SLOTS    = (CAL_END_HOUR - CAL_START_HOUR) * (60 / SLOT_MINS);

const ALL_DOW        = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHORT_MONTHS   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function slotToTime(slot) {
  const m = CAL_START_HOUR * 60 + slot * SLOT_MINS;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

function timeToSlot(time) {
  const [h, m] = time.split(':').map(Number);
  return (h - CAL_START_HOUR) * (60 / SLOT_MINS) + m / SLOT_MINS;
}

// Returns Monday 00:00:00 of the week containing d
function getWeekStart(d) {
  const date = new Date(d);
  const day  = date.getDay();                    // 0=Sun
  date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatShort(d) {
  return `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function formatWeekRange(weekStart) {
  const end = addDays(weekStart, 6);
  if (weekStart.getMonth() === end.getMonth()) {
    return `${SHORT_MONTHS[weekStart.getMonth()]} ${weekStart.getDate()}–${end.getDate()}, ${weekStart.getFullYear()}`;
  }
  return `${formatShort(weekStart)} – ${formatShort(end)}, ${end.getFullYear()}`;
}

function getDow(d) {
  return ALL_DOW[d.getDay() === 0 ? 6 : d.getDay() - 1];
}

// Builds the day-cell array for a month calendar (Mon-start grid, padded to multiples of 7)
function getMonthGrid(firstOfMonth) {
  const year   = firstOfMonth.getFullYear();
  const month  = firstOfMonth.getMonth();
  const rawDay = firstOfMonth.getDay();
  const offset = rawDay === 0 ? 6 : rawDay - 1;
  const total  = new Date(year, month + 1, 0).getDate();
  const cells  = [];
  for (let i = offset; i > 0; i--) cells.push({ date: new Date(year, month, 1 - i), current: false });
  for (let i = 1; i <= total; i++)  cells.push({ date: new Date(year, month, i),    current: true  });
  while (cells.length % 7 !== 0) {
    const prev = cells[cells.length - 1].date;
    cells.push({ date: addDays(prev, 1), current: false });
  }
  return cells;
}

/* ─── Availability fit helpers ─── */
function parseTo24h(timeStr) {
  const [timePart, ampm] = timeStr.split(' ');
  let [h, m] = timePart.split(':').map(Number);
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function addMinutes(time24, mins) {
  const [h, m] = time24.split(':').map(Number);
  const t = h * 60 + m + mins;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

// Checks whether the full session [start, start+duration] fits inside any availability
// block set for the specific calendar date.
function sessionFitsAvailability(availability, date, timeStr, durationMins) {
  const start = parseTo24h(timeStr);
  const end   = addMinutes(start, durationMins);
  return (availability[date] || []).some(b => start >= b.start && end <= b.end);
}

/* ─── Server-backed data ───────────────────────────────────────────────────
   New consultant accounts start completely clean. Schedule and pending
   requests will be fetched from their respective endpoints when those are
   implemented; availability is loaded from GET /consultant/availability
   inside the main component below and persisted via PUT on save.
   ────────────────────────────────────────────────────────────────────────── */

/* ─── Inline SVG icons ─── */
const HomeIcon     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const InboxIcon    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>;
const CalendarIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const ClockIcon    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const SignOutIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
const CheckIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const XIcon        = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const LockIcon     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
const MailIcon     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 8l10 6 10-6"/></svg>;

/* ─── Shared micro-components ─── */

function StatCard({ icon, iconClass, value, label }) {
  return (
    <div className="portal-stat-card">
      <div className={`psc-icon ${iconClass}`}>{icon}</div>
      <div className="psc-value">{value}</div>
      <div className="psc-label">{label}</div>
    </div>
  );
}

function PortalPanel({ title, children }) {
  return (
    <div className="portal-panel">
      <div className="portal-panel-header">
        <span className="portal-panel-title">{title}</span>
      </div>
      {children}
    </div>
  );
}

function PortalEmptyState({ title, message, icon }) {
  return (
    <div className="portal-empty-state">
      {icon && <div className="pes-icon">{icon}</div>}
      <p className="pes-title">{title}</p>
      {message && <p className="pes-text">{message}</p>}
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`pill pill-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
}

/* ─── Today's session count helper ─── */
function countToday(sessions) {
  const today = new Date().toISOString().split('T')[0];
  return sessions.filter(s => s.date === today).length;
}

/* ─── Overview view ─── */
function OverviewView({ pendingCount, schedule }) {
  const todayCount = countToday(schedule);
  const weekCount  = schedule.length;

  return (
    <>
      <div className="portal-stat-grid">
        <StatCard icon={<CalendarIcon />} iconClass="psc-icon-aero"  value={todayCount}  label="Today's Sessions"   />
        <StatCard icon={<CalendarIcon />} iconClass="psc-icon-sky"   value={weekCount}   label="Upcoming This Week"  />
        <StatCard icon={<InboxIcon />}    iconClass={pendingCount > 0 ? 'psc-icon-gold' : 'psc-icon-navy'} value={pendingCount} label="Pending Requests" />
      </div>

      <PortalPanel title="Upcoming Sessions (Next 3)">
        {schedule.length === 0
          ? <PortalEmptyState icon={<CalendarIcon />} title="No upcoming sessions" />
          : (
            <div className="portal-table-wrap">
              <table className="portal-table">
                <thead>
                  <tr><th>Date</th><th>Time</th><th>Child</th><th>Service</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {schedule.slice(0, 3).map(s => (
                    <tr key={s.id}>
                      <td className="td-primary">{s.date}</td>
                      <td>{s.time}</td>
                      <td>{s.childName}</td>
                      <td>{s.service}</td>
                      <td><StatusPill status={s.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </PortalPanel>
    </>
  );
}

/* ─── Pending requests view ─── */
function PendingRequestsView({ requests, availability, onAccept, onDecline }) {
  function formatRelative(isoString) {
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  return (
    <>
      {requests.length === 0
        ? (
          <div className="portal-panel">
            <PortalEmptyState
              icon={<InboxIcon />}
              title="No pending requests"
              message="New session requests will appear here for your review."
            />
          </div>
        )
        : (
          <div className="requests-list">
            {requests.map(req => {
              const fits     = sessionFitsAvailability(availability, req.date, req.time, req.duration);
              const hasAvail = (availability[req.date] || []).length > 0;
              const dateLabel = formatShort(new Date(req.date + 'T12:00:00'));

              let availMsg, availOk;
              if (fits) {
                availMsg = '✓ Fits your availability';
                availOk  = true;
              } else if (!hasAvail) {
                availMsg = `⚠ No availability set for ${dateLabel}`;
                availOk  = false;
              } else {
                availMsg = `⚠ Session overruns your ${dateLabel} availability`;
                availOk  = false;
              }

              return (
                <div key={req.id} className="request-card">
                  <div className="request-card-info">
                    <p className="request-card-child">{req.childName}</p>
                    <p className="request-card-meta">
                      Parent: {req.parentName} · {req.parentEmail}
                    </p>
                    <p className="request-card-service">{req.service}</p>
                    <p className="request-card-datetime">
                      Requested for <strong>{req.date}</strong> at <strong>{req.time}</strong>
                      {' '}· {req.duration} min
                      <span className="request-card-age">· {formatRelative(req.requestedAt)}</span>
                    </p>
                    <p className={`request-card-avail ${availOk ? 'avail-ok' : 'avail-warn'}`}>
                      {availMsg}
                    </p>
                  </div>
                  <div className="request-card-actions">
                    {/* TODO: POST /api/consultant/requests/:id/accept */}
                    <button
                      className="btn btn-sm btn-aero"
                      onClick={() => onAccept(req.id)}
                      disabled={!fits}
                      title={fits ? undefined : 'Session falls outside your availability for this date'}
                    >
                      <CheckIcon /> Accept
                    </button>
                    {/* TODO: POST /api/consultant/requests/:id/decline */}
                    <button
                      className="btn btn-sm req-btn-decline"
                      onClick={() => onDecline(req.id)}
                    >
                      <XIcon /> Decline
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </>
  );
}

/* ─── Schedule view ─── */
function ScheduleView({ sessions }) {
  return (
    <PortalPanel title="Confirmed Upcoming Sessions">
      {sessions.length === 0
        ? <PortalEmptyState icon={<CalendarIcon />} title="No upcoming sessions" message="Accepted requests will appear here." />
        : (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr><th>Date</th><th>Time</th><th>Child</th><th>Service</th><th>Status</th></tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id}>
                    <td className="td-primary">{s.date}</td>
                    <td>{s.time}</td>
                    <td>{s.childName}</td>
                    <td>{s.service}</td>
                    <td><StatusPill status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </PortalPanel>
  );
}

/* ─── Week grid view ─────────────────────────────────────────────────────────
   Shows 7 specific calendar dates as columns. Drag within a column to set
   availability windows for that date. Each week is independent.
   ─────────────────────────────────────────────────────────────────────────── */
function WeekGridView({ weekStart, setWeekStart, todayKey, availability, setAvailability }) {
  const [dragState, setDragState] = useState(null);
  const dragRef = useRef(null);

  useEffect(() => {
    function onMouseUp() {
      const ds = dragRef.current;
      if (!ds) return;
      const lo = Math.min(ds.startSlot, ds.currentSlot);
      const hi = Math.max(ds.startSlot, ds.currentSlot) + 1;
      if (lo < hi) {
        setAvailability(prev => ({
          ...prev,
          [ds.dateKey]: [...(prev[ds.dateKey] || []), { start: slotToTime(lo), end: slotToTime(hi) }],
        }));
      }
      dragRef.current = null;
      setDragState(null);
    }
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, [setAvailability]);

  function startDrag(dateKey, slotIdx) {
    const state = { dateKey, startSlot: slotIdx, currentSlot: slotIdx };
    dragRef.current = state;
    setDragState(state);
  }

  function updateDrag(dateKey, slotIdx) {
    if (!dragRef.current || dragRef.current.dateKey !== dateKey) return;
    const state = { ...dragRef.current, currentSlot: slotIdx };
    dragRef.current = state;
    setDragState(state);
  }

  function removeBlock(dateKey, idx) {
    setAvailability(prev => ({
      ...prev,
      [dateKey]: prev[dateKey].filter((_, i) => i !== idx),
    }));
  }

  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const slots     = Array.from({ length: TOTAL_SLOTS }, (_, i) => ({
    idx: i, label: slotToTime(i).endsWith(':00') ? slotToTime(i) : '',
  }));

  return (
    <>
      {/* Week navigation */}
      <div className="avail-week-nav">
        <button
          className="avail-month-arrow"
          onClick={() => setWeekStart(d => addDays(d, -7))}
          aria-label="Previous week"
        >‹</button>
        <span className="avail-week-label">{formatWeekRange(weekStart)}</span>
        <button
          className="avail-month-arrow"
          onClick={() => setWeekStart(d => addDays(d, 7))}
          aria-label="Next week"
        >›</button>
        <button
          className="avail-today-btn"
          onClick={() => setWeekStart(getWeekStart(new Date()))}
        >Today</button>
      </div>

      <div className="avail-cal">
        {/* Column headers — day-of-week + date */}
        <div className="avail-cal-header">
          <div className="avail-gutter-head" />
          {weekDates.map(d => {
            const key = formatDateKey(d);
            return (
              <div key={key} className={`avail-col-head${key === todayKey ? ' avail-col-head-today' : ''}`}>
                <span className="avail-col-dow">{getDow(d)}</span>
                <span className="avail-col-date">{formatShort(d)}</span>
              </div>
            );
          })}
        </div>

        {/* Scrollable body */}
        <div className="avail-cal-body">
          <div className="avail-time-gutter">
            {slots.map(s => (
              <div key={s.idx} className="avail-time-label" style={{ height: SLOT_H }}>{s.label}</div>
            ))}
          </div>

          {weekDates.map(d => {
            const dateKey = formatDateKey(d);
            const blocks  = (availability[dateKey] || []).map((w, idx) => ({
              ...w, idx,
              startSlot: timeToSlot(w.start),
              endSlot:   timeToSlot(w.end),
            }));
            const ds        = dragState && dragState.dateKey === dateKey ? dragState : null;
            const previewLo = ds ? Math.min(ds.startSlot, ds.currentSlot) : null;
            const previewHi = ds ? Math.max(ds.startSlot, ds.currentSlot) + 1 : null;

            return (
              <div
                key={dateKey}
                className={`avail-day-col${dateKey === todayKey ? ' avail-day-col-today' : ''}`}
              >
                {slots.map(s => (
                  <div
                    key={s.idx}
                    className={`avail-slot${s.label ? ' avail-slot-hour' : ''}`}
                    style={{ height: SLOT_H }}
                    onMouseDown={e => { e.preventDefault(); startDrag(dateKey, s.idx); }}
                    onMouseEnter={() => updateDrag(dateKey, s.idx)}
                  />
                ))}

                {blocks.map(b => (
                  <div
                    key={b.idx}
                    className="avail-block"
                    style={{ top: b.startSlot * SLOT_H, height: (b.endSlot - b.startSlot) * SLOT_H }}
                  >
                    <span className="avail-block-label">{b.start}–{b.end}</span>
                    <button
                      className="avail-block-remove"
                      onMouseDown={e => e.stopPropagation()}
                      onClick={e => { e.stopPropagation(); removeBlock(dateKey, b.idx); }}
                      aria-label={`Remove ${b.start}–${b.end} on ${dateKey}`}
                    >×</button>
                  </div>
                ))}

                {previewLo !== null && (
                  <div
                    className="avail-block avail-block-preview"
                    style={{ top: previewLo * SLOT_H, height: (previewHi - previewLo) * SLOT_H, pointerEvents: 'none' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <p className="avail-hint" style={{ padding: '8px 24px 0', margin: 0 }}>
        Click and drag to set availability · Click × to remove · Use the arrows to navigate weeks
      </p>
    </>
  );
}

/* ─── Month overview ─────────────────────────────────────────────────────────
   Read-only calendar that shows which dates have availability set.
   Clicking any date navigates the week view to that date's week.
   ─────────────────────────────────────────────────────────────────────────── */
function MonthOverview({ availability, todayKey, onSelectDate }) {
  const [viewDate, setViewDate] = useState(() => {
    const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const cells      = getMonthGrid(viewDate);
  const monthLabel = viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="avail-monthly">
      <div className="avail-month-nav">
        <button
          className="avail-month-arrow"
          onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          aria-label="Previous month"
        >‹</button>
        <span className="avail-month-label">{monthLabel}</span>
        <button
          className="avail-month-arrow"
          onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          aria-label="Next month"
        >›</button>
      </div>

      <div className="avail-month-grid">
        {ALL_DOW.map(d => <div key={d} className="avail-month-dow">{d}</div>)}

        {cells.map(({ date, current }) => {
          const key     = formatDateKey(date);
          const windows = availability[key] || [];
          const isToday = key === todayKey;

          return (
            <div
              key={key}
              className={[
                'avail-month-cell',
                !current         ? 'avail-month-cell-other'    : '',
                isToday          ? 'avail-month-cell-today'     : '',
                windows.length > 0 ? 'avail-month-cell-has-avail' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => current && onSelectDate(date)}
              title={current ? (windows.length > 0 ? 'Click to edit this week' : 'No availability — click to add') : undefined}
            >
              <span className={`avail-month-day-num${isToday ? ' avail-month-day-today' : ''}`}>
                {date.getDate()}
              </span>
              {windows.map((w, i) => (
                <span key={i} className="avail-month-window">{w.start}–{w.end}</span>
              ))}
              {current && windows.length === 0 && (
                <span className="avail-month-no-avail">—</span>
              )}
            </div>
          );
        })}
      </div>

      <p className="avail-month-hint">Click any date to edit that week's availability</p>
    </div>
  );
}

/* ─── Availability editor ─── */
function AvailabilityEditor({ availability, setAvailability, loadError, loading, onSave }) {
  const [calView,   setCalView]   = useState('week');
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [saved,     setSaved]     = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);
  const todayKey = formatDateKey(new Date());

  function goToWeek(date) {
    setWeekStart(getWeekStart(date));
    setCalView('week');
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await onSave(availability);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveError(err.message || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="portal-panel">
      <div className="portal-panel-header">
        <span className="portal-panel-title">Availability</span>
        <div className="avail-view-tabs">
          <button
            className={`avail-tab${calView === 'week' ? ' active' : ''}`}
            onClick={() => setCalView('week')}
          >Week</button>
          <button
            className={`avail-tab${calView === 'month' ? ' active' : ''}`}
            onClick={() => setCalView('month')}
          >Month</button>
        </div>
      </div>

      {loading && (
        <p style={{ padding: '20px 24px', margin: 0, color: 'var(--slate)' }}>
          Loading your availability…
        </p>
      )}

      {loadError && !loading && (
        <p style={{ padding: '20px 24px', margin: 0, color: '#c0392b' }}>
          {loadError}
        </p>
      )}

      {!loading && calView === 'week' && (
        <WeekGridView
          weekStart={weekStart}
          setWeekStart={setWeekStart}
          todayKey={todayKey}
          availability={availability}
          setAvailability={setAvailability}
        />
      )}

      {!loading && calView === 'month' && (
        <MonthOverview
          availability={availability}
          todayKey={todayKey}
          onSelectDate={goToWeek}
        />
      )}

      <div className="avail-footer">
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving…' : saved ? <><CheckIcon /> Saved!</> : 'Save Availability'}
        </button>
        {saved && !saveError && <span className="avail-saved-note">Changes saved successfully.</span>}
        {saveError && (
          <span className="avail-saved-note" style={{ color: '#c0392b' }}>
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Main component ─── */
export default function ConsultantPortal() {
  const user = usePortalUser();
  const [activeView,   setActiveView]   = useState('overview');
  // New consultants start clean — pending requests, schedule, and
  // availability are all backed by the API and empty until populated.
  const [pendingReqs,  setPendingReqs]  = useState([]);
  const [schedule]                      = useState([]);
  const [availability, setAvailability] = useState({});
  const [availLoading, setAvailLoading] = useState(true);
  const [availError,   setAvailError]   = useState(null);

  // Cognito ID token is required for /consultant/* routes; cache the
  // accessor so save and load share the same auth path.
  const authHeaders = useCallback(async () => {
    const session = await getCurrentSession();
    if (!session?.idToken) throw new Error('Not signed in');
    return {
      Authorization: `Bearer ${session.idToken}`,
      'Content-Type': 'application/json',
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API_BASE}/consultant/availability`, { headers });
        if (cancelled) return;
        if (res.status === 404) {
          // A signed-in consultant with no row in StratosConsultants — most
          // likely an admin half-created them. Show empty state instead of
          // crashing; the Save button will fail the same way until the row
          // exists, which is the correct signal.
          setAvailability({});
          setAvailError('Your consultant record is missing — contact an administrator.');
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Failed to load availability (HTTP ${res.status})`);
        }
        const data = await res.json();
        setAvailability(data.availability || {});
      } catch (err) {
        if (!cancelled) setAvailError(err.message || 'Failed to load availability');
      } finally {
        if (!cancelled) setAvailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const saveAvailability = useCallback(async (next) => {
    const headers = await authHeaders();
    const res = await fetch(`${API_BASE}/consultant/availability`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ availability: next }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save (HTTP ${res.status})`);
    }
    const data = await res.json();
    // The server returns the normalized form (sorted windows). Adopt it so
    // the UI mirrors what the booking page will see.
    setAvailability(data.availability || {});
  }, [authHeaders]);

  function handleAccept(id) {
    const req = pendingReqs.find(r => r.id === id);
    if (!req || !sessionFitsAvailability(availability, req.date, req.time, req.duration)) return;
    // TODO: POST /api/consultant/requests/:id/accept  then remove from list
    setPendingReqs(prev => prev.filter(r => r.id !== id));
  }

  function handleDecline(id) {
    // TODO: POST /api/consultant/requests/:id/decline  then remove from list
    setPendingReqs(prev => prev.filter(r => r.id !== id));
  }

  const navItems = [
    { id: 'overview',     label: 'Overview',        icon: <HomeIcon /> },
    { id: 'requests',     label: 'Pending Requests', icon: <InboxIcon />,   badge: pendingReqs.length },
    { id: 'schedule',     label: 'My Schedule',      icon: <CalendarIcon /> },
    { id: 'availability', label: 'Availability',     icon: <ClockIcon /> },
    { id: 'email',        label: 'Email',            icon: <MailIcon /> },
    { id: 'account',      label: 'Account',          icon: <LockIcon /> },
  ];

  const activeNavLabel = navItems.find(n => n.id === activeView)?.label ?? 'Overview';

  return (
    <div className="portal-shell">
      {/* ── Sidebar ── */}
      <aside className="portal-sidebar">
        <div className="sidebar-logo">
          <span className="sidebar-logo-circle">
            <img src="/stratos-logo.png" alt="Stratos" className="sidebar-logo-img" />
          </span>
          <span className="sidebar-logo-text">
            <span style={{ color: 'var(--sky)', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Consultant Portal</span>
          </span>
        </div>

        <nav className="sidebar-nav" aria-label="Consultant portal navigation">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${activeView === item.id ? 'active' : ''}`}
              onClick={() => setActiveView(item.id)}
              aria-current={activeView === item.id ? 'page' : undefined}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              {item.label}
              {item.badge != null && item.badge > 0 && (
                <span className="sidebar-badge">{item.badge}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="sidebar-user-footer">
          <div className="sidebar-user-row">
            <div className="sidebar-avatar">{user.name.charAt(0)}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.name}</div>
              <div className="sidebar-user-role">{user.role}</div>
            </div>
          </div>
          <button className="sidebar-signout" onClick={user.signOut}><SignOutIcon /> Sign Out</button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="portal-main">
        <div className="portal-topbar">
          <span className="portal-topbar-title">{activeNavLabel}</span>
          {pendingReqs.length > 0 && activeView !== 'requests' && (
            <button
              className="btn btn-sm"
              style={{ borderColor: 'var(--gold)', color: 'var(--gold)', background: 'transparent', border: '2px solid' }}
              onClick={() => setActiveView('requests')}
            >
              {pendingReqs.length} pending request{pendingReqs.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>

        <div className="portal-body">
          {activeView === 'overview'     && <OverviewView     pendingCount={pendingReqs.length} schedule={schedule} />}
          {activeView === 'requests'     && <PendingRequestsView requests={pendingReqs} availability={availability} onAccept={handleAccept} onDecline={handleDecline} />}
          {activeView === 'schedule'     && <ScheduleView     sessions={schedule} />}
          {activeView === 'availability' && (
            <AvailabilityEditor
              availability={availability}
              setAvailability={setAvailability}
              loading={availLoading}
              loadError={availError}
              onSave={saveAvailability}
            />
          )}
          {activeView === 'email'        && <EmailClient />}
          {activeView === 'account'      && <ChangePasswordForm />}
        </div>
      </div>
    </div>
  );
}
